/**
 * Environment detection utilities for bucket selection
 */
import { env } from "@repo/env";

/**
 * True for hostnames that can only be a developer's own machine.
 *
 * Shared with `getBranchPrefix` so the two never disagree about what "local"
 * means. `*.localhost` matters as much as bare `localhost`: the dev-test skill
 * derives a per-worktree hostname from the directory basename (see
 * `.agents/skills/dev-test/SKILL.md`), so local sessions arrive as e.g.
 * `eng-1466-clients.localhost`.
 */
export function isLocalHostname(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0" ||
    hostname === "[::1]" ||
    hostname === "::1" ||
    hostname.endsWith(".localhost") ||
    hostname.endsWith(".local")
  );
}

export function getEnvironment(): "dev" | "staging" | "prod" {
  // In server-side contexts, check VERCEL_ENV first, then fall back to URL/NODE_ENV
  if (typeof window === "undefined") {
    // Server-side detection
    const nodeEnv = env.NODE_ENV;
    const vercelEnv = env.VERCEL_ENV;
    const triggerEnv = env.TRIGGER_ENVIRONMENT;
    const vercelUrl = env.VERCEL_URL;
    const nextPublicBaseUrl = env.NEXT_PUBLIC_BASE_URL;

    // PRIORITY 0: Trigger.dev environment (injected by trigger.config.ts syncEnvVars)
    if (triggerEnv) {
      const normalized = triggerEnv.toLowerCase();
      if (normalized === "prod" || normalized === "production") {
        return "prod";
      }
      if (normalized === "staging") {
        return "staging";
      }
      return "dev"; // preview, dev, feature branches
    }

    // PRIORITY 1: Use VERCEL_ENV if available (most reliable for Vercel deployments)
    if (vercelEnv) {
      switch (vercelEnv) {
        case "production":
          return "prod";
        case "preview":
          return "staging";
        case "development":
          return "dev";
      }
    }

    // PRIORITY 2: Check explicit staging URLs or base URL
    if (
      vercelUrl?.includes("preview") ||
      nextPublicBaseUrl?.includes("preview.terac.com") ||
      vercelUrl?.includes("platform-") ||
      nextPublicBaseUrl?.includes("staging")
    ) {
      return "staging";
    }

    // PRIORITY 3: Check production URLs (custom domain)
    if (
      vercelUrl === "terac.com" ||
      nextPublicBaseUrl === "https://terac.com" ||
      vercelUrl === "www.terac.com" ||
      nextPublicBaseUrl === "https://www.terac.com"
    ) {
      return "prod";
    }

    // PRIORITY 4: Default to dev for local development
    if (nodeEnv === "development") {
      return "dev";
    }

    // PRIORITY 5: Fallback to prod for production NODE_ENV
    return nodeEnv === "production" ? "prod" : "dev";
  }
  // Client-side detection. This function also selects S3 buckets and LiveKit
  // prefixes, so a misclassification is not limited to telemetry.
  const hostname = window.location.hostname;

  // PRIORITY 0: local development
  if (isLocalHostname(hostname)) {
    return "dev";
  }

  // PRIORITY 1: preview, which lives under the *.terac.com suffix matched below
  if (
    hostname === "preview.terac.com" ||
    hostname.endsWith(".preview.terac.com")
  ) {
    return "staging";
  }

  // PRIORITY 2: the production apex plus any prod subdomain
  if (hostname === "terac.com" || hostname.endsWith(".terac.com")) {
    return "prod";
  }

  // PRIORITY 3: Google Translate serves a proxied copy of a public page from
  // `<host-with-dashes>.translate.goog`, so these are real production users.
  if (hostname.endsWith(".translate.goog")) {
    return "prod";
  }

  // PRIORITY 4: Vercel-generated deployment URLs are only ever previews; the
  // production deployment is served from the custom domain handled above.
  if (hostname.endsWith(".vercel.app")) {
    return "staging";
  }

  // Everything still unmatched (tunnels, raw IPs, unknown proxies) stays dev:
  // a dev host misread as prod pollutes production telemetry, which is far more
  // expensive to undo than a prod host misread as dev.
  return "dev";
}

export function getInterviewRecordingsBucket(
  envOverride?: "dev" | "staging" | "prod",
): string {
  const deployEnv = envOverride ?? getEnvironment();

  // When an explicit envOverride is provided, use environment-specific buckets directly
  // (don't fall back to AWS_S3_BUCKET_NAME which would ignore the override)
  if (envOverride) {
    switch (deployEnv) {
      case "dev":
        return env.AWS_INTERVIEW_RECORDINGS_BUCKET_DEV ?? "terac-platform-dev";
      case "staging":
        return (
          env.AWS_INTERVIEW_RECORDINGS_BUCKET_STAGING ??
          "terac-platform-staging"
        );
      case "prod":
        return env.AWS_INTERVIEW_RECORDINGS_BUCKET ?? "terac-platform-prod";
    }
  }

  // Auto-detected environment: use AWS_S3_BUCKET_NAME if set (backwards compatible)
  let bucket: string;
  switch (deployEnv) {
    case "dev":
      bucket =
        env.AWS_S3_BUCKET_NAME ??
        env.AWS_INTERVIEW_RECORDINGS_BUCKET_DEV ??
        "terac-platform-dev";
      break;
    case "staging":
      bucket =
        env.AWS_S3_BUCKET_NAME ??
        env.AWS_INTERVIEW_RECORDINGS_BUCKET_STAGING ??
        "terac-platform-staging";
      break;
    case "prod":
    default:
      bucket =
        env.AWS_S3_BUCKET_NAME ??
        env.AWS_INTERVIEW_RECORDINGS_BUCKET ??
        "terac-platform-prod";
      break;
  }

  return bucket;
}

export function getInterviewStimuliBucket(
  envOverride?: "dev" | "staging" | "prod",
): string {
  const deployEnv = envOverride ?? getEnvironment();

  // When an explicit envOverride is provided, use environment-specific buckets directly
  if (envOverride) {
    switch (deployEnv) {
      case "dev":
        return env.AWS_INTERVIEW_STIMULI_BUCKET_DEV ?? "terac-platform-dev";
      case "staging":
        return (
          env.AWS_INTERVIEW_STIMULI_BUCKET_STAGING ?? "terac-platform-staging"
        );
      case "prod":
        return env.AWS_INTERVIEW_STIMULI_BUCKET ?? "terac-platform-prod";
    }
  }

  // Auto-detected environment: use AWS_S3_BUCKET_NAME if set (backwards compatible)
  switch (deployEnv) {
    case "dev":
      return (
        env.AWS_S3_BUCKET_NAME ??
        env.AWS_INTERVIEW_STIMULI_BUCKET_DEV ??
        "terac-platform-dev"
      );
    case "staging":
      return (
        env.AWS_S3_BUCKET_NAME ??
        env.AWS_INTERVIEW_STIMULI_BUCKET_STAGING ??
        "terac-platform-staging"
      );
    case "prod":
    default:
      return (
        env.AWS_S3_BUCKET_NAME ??
        env.AWS_INTERVIEW_STIMULI_BUCKET ??
        "terac-platform-prod"
      );
  }
}
